/* What the ranges page knows: the active collection's charts, in the picker's order, where
   the tree stands, which mode the page is in, and the drafts being written. One object, so a
   redraw reads a single place and nothing is a global of its own. */

import {
  ACTIONS,
  PROFILES_MAX,
  branchesFor,
  indexSpots,
  initialTree,
  opponentName,
  seatsFor,
  versus,
  type Action,
  type EquityTable,
  type Notes,
  type Opponent,
  type SeatDraft,
  type Spot,
  type TreeState,
  type WrittenAction,
  seatName,
} from "@preflop/core";
import type { Session } from "../../state/session";
import { saveSetting } from "../../state/settings";

/** The chart open in the editor: both the text and the last picture it parsed into, kept
    across redraws so a repaint does not lose what was typed. */
export interface EditDraft {
  key: string;
  notes: Notes;
  /** The size hero's raise is made to, as the box holds it. Empty is the table's price. */
  raise: string;
  preview: Spot | null;
  error: string;
  warn: string;
  /** Hands the notation names that hero does not hold here. Non-empty refuses the save. */
  outside: string[];
  /** The action last typed into: it is served its weight in full, the others trimmed. */
  priority: WrittenAction | null | undefined;
}

/** A spot being described in the creator. */
export interface CreatorDraft extends SeatDraft {
  stakes: string;
  format: string;
  stack: string;
  openSize: string;
  branch: string | null;
  /** The opponent profile the chart is written against, or null for `Basic Strategy`. */
  profile: string | null;
  /** Where hero's range comes from: "full", "typed", "cont:<key>" or the key copied. */
  from: string;
  rangeNote: string;
  rangeFloor: number;
  /** Whether the notation was typed into: a copied chart stops overwriting it then. */
  touched: boolean;
  notes: Notes;
  /** As in an edit draft: the size hero's raise is made to, or empty for the table's. */
  raise: string;
  priority: WrittenAction | null | undefined;
  moved: string[];
  /** Which ladder has its free-text field open. */
  typing: { stack: boolean; openSize: boolean };
  /** The last picture the draft parsed into. */
  preview: Spot | null;
}

/** The two ladders with no closed list: a rung can be typed in. */
export type Ladder = "stack" | "openSize";
export type CatalogAdds = Record<Ladder, string[]>;

export interface Page {
  session: Session;
  /** The collection's charts in the picker's order. Rebuilt after every save or delete. */
  spots: Spot[];
  /** Where a save goes while the active collection is not the user's own: the id of one of
      their collections, or "new" for a new copy of what is on screen. */
  target: string;
  /** Charts an older build left on this device, waiting to be moved into a collection. */
  waiting: number;
  index: Map<string, Spot>;
  tree: TreeState;
  /** The chart the tree stands on, as of the last render. */
  spot: Spot | null;
  isolate: Action | null;
  // Editing is a mode the whole page is in, not a state of one panel. Sticky across reloads:
  // someone filling a gap in the tree does it over several sittings.
  editMode: boolean;
  /** Null until the table arrives, and for good when it could not be read. */
  equity: EquityTable | null;
  equityLoaded: boolean;
  // Text typed but not saved. Every navigation rebuilds the notation panel, so without a
  // guard a click on a seat would throw the work away without a word.
  dirty: boolean;
  // Which sidebar ladder has its "+" field open, if any. Navigating closes it: render()
  // clears this before the column is drawn again.
  adding: Ladder | null;
  editDraft: EditDraft | null;
  /** The creator's draft, or null when the panel is shut. */
  creator: CreatorDraft | null;
  /* Rungs typed into the sidebar's "+" that nothing is charted on yet. They are catalog
     entries, not spots: the button shows up greyed, and the chart behind it is written the
     day someone clicks it. */
  adds: CatalogAdds;
}

/* The collection arrives sorted by key, which is alphabetical: BB before LJ, "Called vs.
   Squeeze" before RFI. The tree snaps a half-made choice onto the first chart it finds, so
   the list is put in the order the picker draws its buttons in, and the first chart found is
   the one under the first button: the page opens on the first seat to act, raising first in. */
const STAKES_ORDER = ["NL10-25", "NL50-100", "NL200-500"];
const GAME_TYPES = ["Cash Games", "HU Cash"];

function inTreeOrder(spots: Spot[], opponents: readonly Opponent[]): Spot[] {
  /* The order the collection lists a profile in. A variation naming a profile the collection
     no longer holds sorts after the ones it does, not in front of all of them. */
  const profileAt = (id: string): number => {
    const at = opponents.findIndex((o) => o.id === id);
    return at < 0 ? PROFILES_MAX : at;
  };
  const at = (list: readonly string[], v: string | undefined): number => {
    const i = v === undefined ? -1 : list.indexOf(v);
    return i < 0 ? list.length : i;
  };
  const size = (v: string): number => parseFloat(v) || 0;
  const rank = (s: Spot): number[] => {
    const seats = seatsFor(s.gameType);
    return [
      at(STAKES_ORDER, s.stakes),
      at(GAME_TYPES, s.gameType),
      -size(s.stack),
      size(s.openSize) || 99,
      at(seats, s.hero),
      at(ACTIONS, s.action),
      at(seats, s.seats[0]),
      at(seats, s.seats[1]),
      at(branchesFor(s.action) ?? [], s.branch),
      // Last, as the picker reads it: the chart itself first, then its variations in the
      // order the collection lists the profiles.
      s.profile ? 1 + profileAt(s.profile) : 0,
    ];
  };
  return spots
    .map((spot) => ({ spot, rank: rank(spot) }))
    .sort((a, b) => {
      for (let i = 0; i < a.rank.length; i++) {
        const d = (a.rank[i] ?? 0) - (b.rank[i] ?? 0);
        if (d) return d;
      }
      return a.spot.key.localeCompare(b.spot.key);
    })
    .map((r) => r.spot);
}

let held: Page | null = null;

/** The page's state. Reading it before boot has filled it in is a bug, so throw. */
export const page = (): Page => {
  if (!held) throw new Error("the ranges page has not booted yet");
  return held;
};

export function openPage(
  session: Session,
  opts: { editMode: boolean; adds: unknown; target: string; waiting: number },
): Page {
  const spots = inTreeOrder(session.spots, session.collection.opponents);
  const { editMode } = opts;
  held = {
    session,
    spots,
    target: opts.target,
    waiting: opts.waiting,
    index: indexSpots(spots),
    tree: initialTree(spots),
    spot: null,
    isolate: null,
    editMode,
    equity: null,
    equityLoaded: false,
    dirty: false,
    adding: null,
    editDraft: null,
    creator: null,
    adds: readAdds(opts.adds),
  };
  refreshCatalog();
  return held;
}

/** After a save or a delete: the list every lookup reads, built again. */
export function reloadSpots(): void {
  const p = page();
  p.spots = inTreeOrder(p.session.spots, p.session.collection.opponents);
  p.index = indexSpots(p.spots);
  refreshCatalog();
}

/* ---- the full redraw ----
   Nearly every panel ends a click by asking for one, and the redraw draws every panel, so
   the call goes through here rather than through a circle of imports. */
let redraw = (): void => {};
export const render = (): void => redraw();
export const onRender = (f: () => void): void => void (redraw = f);

export function confirmDiscard(): boolean {
  const p = page();
  if (!p.dirty) return true;
  if (!confirm("Discard unsaved notation changes?")) return false;
  p.dirty = false;
  return true;
}

/** Point the tree at a spot, whatever branch or profile it sits on. */
export function jumpTo(sp: Spot): void {
  const p = page();
  p.tree = {
    stakes: sp.stakes,
    gameType: sp.gameType,
    format: sp.format,
    stack: sp.stack,
    openSize: sp.openSize,
    hero: sp.hero,
    action: sp.action,
    seats: [...sp.seats],
    branch: sp.branch ?? null,
    profile: sp.profile ?? null,
  };
  p.isolate = null;
}

/** The opponent profiles the active collection knows about, in its own order. */
export const profiles = (): Opponent[] => page().session.collection.opponents;

/** What a profile is called here, by id. */
export const profileName = (id: string): string => opponentName(profiles(), id);

/** A chart named in full, for a list that mixes every rung of the tree. */
export const spotLabel = (s: Spot): string =>
  [
    s.stakes,
    s.gameType,
    s.format,
    s.stack,
    seatName(s.hero),
    s.action,
    ...s.seats.map(seatName),
    ...(s.branch ? [s.branch] : []),
    ...(s.profile ? [versus(profiles(), s.profile)] : []),
  ].join(" | ") + ` · ${s.openSize}`;

/* ---- the catalog ----
   Options as the source offers them. Those without data are greyed out, so gaps in the
   collection are visible at a glance. Seats are not a flat list — they depend on the table,
   see seatsFor(). */
export interface Catalog {
  stakes: string[];
  gameType: string[];
  format: string[];
  stack: string[];
  openSize: string[];
  action: readonly string[];
}

export const CATALOG: Catalog = {
  stakes: [],
  gameType: GAME_TYPES,
  format: ["6Max"],
  stack: [],
  openSize: [],
  action: ACTIONS,
};

// Typing fields are free text; these are only the rungs worth offering. The selector buttons
// keep listing what the collection actually holds, so the read-only view does not fill up
// with sizes nobody charted.
const SUGGEST: Readonly<Record<Ladder, readonly string[]>> = {
  stack: ["300bb", "200bb", "150bb", "125bb", "100bb", "50bb"],
  openSize: ["2x", "2.25x", "2.5x", "3x", "3.5x"],
};
const num = (v: string): number => parseFloat(v) || 0;
export const suggestions = (key: Ladder): string[] =>
  [...new Set([...CATALOG[key], ...SUGGEST[key]])].sort((a, b) =>
    key === "stack" ? num(b) - num(a) : num(a) - num(b),
  );

function readAdds(raw: unknown): CatalogAdds {
  const out: CatalogAdds = { stack: [], openSize: [] };
  if (typeof raw !== "object" || raw === null) return out;
  for (const k of ["stack", "openSize"] as const) {
    const list: unknown = (raw as Record<string, unknown>)[k];
    if (Array.isArray(list)) out[k] = list.filter((v): v is string => typeof v === "string");
  }
  return out;
}

const writeAdds = (): void => void saveSetting("catalogAdds", page().adds);

export function refreshCatalog(): void {
  const p = page();
  const base: Record<Ladder, Set<string>> = {
    openSize: new Set(["2.5x", ...p.spots.map((s) => s.openSize)]),
    stack: new Set(["100bb", ...p.spots.map((s) => s.stack)]),
  };
  // A rung the collection reached on its own is no longer an addition: it is dropped rather
  // than kept as a second copy of the same value.
  for (const k of ["stack", "openSize"] as const) {
    const kept = p.adds[k].filter((v) => !base[k].has(v));
    if (kept.length !== p.adds[k].length) {
      p.adds[k] = kept;
      writeAdds();
    }
  }
  // The sets the source publishes. One with no charts yet shows greyed, the same way an
  // uncharted action does, so the gap is visible rather than absent.
  CATALOG.stakes = [...new Set([...STAKES_ORDER, ...p.spots.map((s) => s.stakes)])].filter(Boolean);
  CATALOG.openSize = [...base.openSize, ...p.adds.openSize].sort(
    (a, b) => (parseFloat(a) || 99) - (parseFloat(b) || 99),
  );
  CATALOG.stack = [...base.stack, ...p.adds.stack].sort(
    (a, b) => (parseFloat(b) || 0) - (parseFloat(a) || 0),
  );
}

/** Adds a rung to a ladder. Returns false when there is nothing to add. */
export function addToCatalog(key: Ladder, value: string): boolean {
  const v = value.trim();
  if (!v || CATALOG[key].includes(v)) return false;
  page().adds[key].push(v);
  writeAdds();
  refreshCatalog();
  return true;
}
